<template>
  <transition name="loader-fade">
    <div v-if="isActive" class="loader" data-cy="loader">
      <div class="loader-overlay" :style="{ opacity: overlayOpacity, backgroundColor: overlayColor }" />
      <div class="loader-content">
        <div class="loader-spinner">
          <svg viewBox="0 0 80 80">
            <circle cx="40" cy="40" r="32" />
          </svg>
        </div>

        <transition name="loader-text-fade" mode="out-in">
          <div :key="text" class="loader-text" v-html="text" />
        </transition>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'ComponentLoader',

  props: {
    active: {
      type: Boolean,
      default: false
    },

    programmatic: {
      type: Boolean,
      default: false
    },

    overlayOpacity: {
      type: Number,
      default: 0.75
    },

    overlayColor: {
      type: String,
      default: '#fff'
    },

    message: {
      type: String,
      default: null
    }
  },

  data() {
    return {
      isActive: this.active,
      text: null,
      delay: 0,
      timer: {
        start: 0,
        end: 0
      }
    }
  },

  watch: {
    message(newData) {
      this.setText(newData)
    }
  },

  beforeMount() {
    if (this.programmatic) {
      document.body.appendChild(this.$el)
    }

    if (this.message) {
      this.setText(this.message)
    }
  },

  mounted() {
    this.show()
  },

  methods: {
    setText(text) {
      if (this.text === null) {
        // If this is the first text then show it immediately
        this.text = text
      } else {
        // If this is not the first text then calculate required delay
        const elapsed = this.$moment().format('x') - this.timer.start // Time elapsed between START and NOW in milliseconds
        // If elpased more time then minimum delay then switch immediatelly, otherwise calculate difference between elapsed time and minimum delay
        const delay = elapsed < this.delay ? this.delay - elapsed : 0

        setTimeout(() => {
          this.text = text
        }, delay)
      }

      this.delay += 1000
    },

    show() {
      this.isActive = true
      this.timer.start = this.$moment().format('x')
    },

    hide(immediate = false) {
      return new Promise((resolve) => {
        if (immediate) {
          this.isActive = false
          this.destroy()
          resolve('hidden')
        } else {
          this.timer.end = this.$moment().format('x')

          const elapsed = this.timer.end - this.timer.start // Time elapsed between START and END in milliseconds
          const delay = elapsed < this.delay ? this.delay - elapsed : 0

          // Delay hiding based on number of messages
          setTimeout(() => {
            this.isActive = false
            this.destroy()
            resolve('hidden')
          }, delay)
        }
      })
    },

    destroy() {
      // Allow the animation to finish before destroying everything
      setTimeout(() => {
        this.$destroy()
        this.removeElement(this.$el)
      }, 500)
    },

    removeElement(el) {
      if (typeof el.remove !== 'undefined') {
        el.remove()
      } else {
        el.parentNode.removeChild(el)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.loader {
  display: flex;
  position: fixed;
  z-index: 999;
  top: 0;
  left: 0;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  overflow: hidden;

  .loader-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: .75;
    background-color: #fff;
  }

  .loader-content {
    .loader-spinner {
      position: relative;
      width: 44px;
      height: 44px;
      margin: auto;

      svg {
        display: block;
        width: 100%;
        height: 100%;

        circle {
          animation: pathCircle 3s cubic-bezier(0.785, 0.135, 0.15, 0.86) infinite;
          stroke-width: 10px;
          stroke-linecap: round;
          stroke-dasharray: (200 / 4 * 3) (200 / 4) (200 / 4 * 3) (200 / 4);
          stroke-dashoffset: 75;
          stroke: $colour-secondary;
          stroke-linejoin: round;
          fill: none;
        }
      }

      &:before {
        content: '';
        display: block;
        position: absolute;
        top: 37px;
        left: 19px;
        width: 6px;
        height: 6px;
        transform: translate(-18px, -18px);
        animation: dotRect 3s cubic-bezier(0.785, 0.135, 0.15, 0.86) infinite;
        border-radius: 50%;
        background: $colour-primary;
      }
    }

    .loader-text {
      position: relative;
      min-height: 1em;
      margin-top: 1em;
      padding: .5em;
      color: $colour-primary;
      font-weight: 500;
    }
  }

  @keyframes dotRect {
    100% {
      transform: translate(0, 0);
    }
    75% {
      transform: translate(18px, -18px);
    }
    50% {
      transform: translate(0, -36px);
    }
    25% {
      transform: translate(-18px, -18px);
    }
    0% {
      transform: translate(0, 0);
    }
  }

  @keyframes pathCircle {
    0% {
      stroke-dashoffset: 325;
    }
    25% {
      stroke-dashoffset: 275;
    }
    50% {
      stroke-dashoffset: 225;
    }
    75% {
      stroke-dashoffset: 175;
    }
    100% {
      stroke-dashoffset: 125;
    }
  }
}

// loader Transition
.loader-fade-enter,
.loader-fade-leave-to {
  opacity: 0;
}

.loader-fade-enter-active,
.loader-fade-leave-active {
  transition: opacity 500ms ease;
}

// Loader Text Transition
.loader-text-fade-enter,
.loader-text-fade-leave-to {
  opacity: 0;
}

.loader-text-fade-enter {
  transform: translateY(50%);
}

.loader-text-fade-leave-to {
  transform: translateY(-50%);
}

.loader-text-fade-enter-active,
.loader-text-fade-leave-active {
  transition-property: opacity, transform;
  transition-duration: 250ms;
  transition-timing-function: ease;
}
</style>
